import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
  TextField,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import { MenuItem } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import api from "../api/axios";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

import PaymentTimeline from "./PaymentTimeline";

export default function FinanceProjects() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchText, setSearchText] = useState("");

  /* ================= TIMELINE MODAL ================= */
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineData, setTimelineData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");

  const handleOpenTimeline = async () => {
    try {
      const { data } = await api.get("/finance/get-schedules");

      setTimelineData(data.data);

      const months = Object.keys(data.data);

      // Current month in same format as dropdown keys
      const currentMonth = dayjs().format("MMMM YYYY");

      // 1️⃣ Exact current month match
      let defaultMonth = months.find((m) => m === currentMonth);

      // 2️⃣ Nearest upcoming month
      if (!defaultMonth) {
        const sortedMonths = months.sort(
          (a, b) =>
            dayjs(a, "MMMM YYYY").valueOf() - dayjs(b, "MMMM YYYY").valueOf(),
        );

        defaultMonth =
          sortedMonths.find((m) =>
            dayjs(m, "MMMM YYYY").isAfter(dayjs(), "month"),
          ) || sortedMonths[0];
      }

      // Set selected month
      setSelectedMonth(defaultMonth);

      setTimelineOpen(true);
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/finance/get-projects");
        console.log(data);
        setRows(
          data.data.map((project, index) => ({
            id: project.id,
            sl: index + 1,
            project_name: project.project_name,
            department: project.department,
            customer: project.customer_name,
            district: project.project_district,
            bd_name: project.bd_name,
            pocName: project.customer_poc_name,
            pocWhatsapp: project.poc_whatsapp,
            workorder_date: project.workorder_date,
            workOrderNumber: project.workorder_number || "",
            contractPeriod: project.deadline,
            qty: project.qty,
            unit: project.unit,
            payment_terms_verified: project.payment_terms_verified,
            created_at: project.created_at,
          })),
        );
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  /* ================= SEARCH ================= */
  const filteredRows = rows.filter((row) =>
    Object.values(row)
      .join(" ")
      .toLowerCase()
      .includes(searchText.toLowerCase()),
  );

  /* ================= EXPORT ================= */
  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(data, "Projects.xlsx");
  };

  /* ================= COLUMNS ================= */
  const columns = [
    { field: "sl", headerName: "SL", width: 80 },
    { field: "department", headerName: "Department", flex: 1 },
    { field: "project_name", headerName: "Project Name", flex: 1 },
    { field: "customer", headerName: "Customer", flex: 1 },
    { field: "district", headerName: "District", flex: 1 },
    { field: "bd_name", headerName: "BD Person", flex: 1 },
    {
      field: "workorder_date",
      headerName: "Workorder Date",
      flex: 1,
      valueFormatter: (params) =>
        params ? dayjs(params.value).format("DD/MM/YYYY") : "-",
    },
    {
      field: "created_at",
      headerName: "Added On",
      flex: 1,
      valueFormatter: (params) =>
        params ? dayjs(params.value).format("DD-MM-YYYY hh:mm a") : "-",
    },
    {
      field: "payment_terms_verified",
      headerName: "Payment Terms Verified",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const isVerified = params.row.payment_terms_verified === 1;

        return isVerified ? (
          <CheckCircleIcon sx={{ color: "#082fdd" }} />
        ) : (
          <CancelIcon sx={{ color: "#ef4444" }} />
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() => {
            navigate(`/finance/projects/${params.row.id}`, {
              state: params.row, // pass data
            });
          }}
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box p={2}>
      <Typography variant="h5" fontWeight={600} mb={2}>
        Project List
      </Typography>

      {/* SEARCH + EXPORT */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <TextField
          size="small"
          label="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={handleOpenTimeline}>
            View Schedule Timeline
          </Button>

          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      {/* DATA GRID */}
      <Box
        sx={{
          height: "80vh",
          width: "100%",
          background: "rgba(255,255,255,0.04)",
          backdropFilter: "blur(10px)",
          borderRadius: 2,
        }}
      >
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          pageSizeOptions={[5, 10, 20, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
          }}
          disableRowSelectionOnClick
          sx={{
            border: "none",
            color: "white",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "rgba(34,197,94,0.15)",
            },
          }}
        />
      </Box>
      <Modal open={timelineOpen} onClose={() => setTimelineOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            maxHeight: "80vh",
            overflowY: "auto",
            p: 3,
            borderRadius: 3,
            backdropFilter: "blur(12px)",
            background: "rgba(0,0,0,0.85)",
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          {/* HEADER */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Payment Schedule Timeline</Typography>

            {/* Month Selector */}
            {timelineData && (
              <TextField
                select
                size="small"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                {Object.keys(timelineData).map((month) => (
                  <MenuItem key={month} value={month}>
                    {month}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          {/* TIMELINE */}
          {timelineData && selectedMonth && (
            <PaymentTimeline data={timelineData} month={selectedMonth} />
          )}
        </Box>
      </Modal>
    </Box>
  );
}
